import { type QueryObserverResult } from '@tanstack/query-core'
import { useQuery } from '@tanstack/react-query'

import { query_get_token_holders } from '@repo/common/queries/admin/fund/query_get_token_holders.ts'
import { graphql_call as graphql_telemetry_call } from '@repo/common/queries/schemas/subgraph_telemetry_query'

export interface TokenHolderData {
  id: string
  wallet_address: string
  balance: string
  first_transaction_at: string
  last_transaction_at: string
  max_balance: string
  transaction_count: string
}

export interface UseTokenHoldersParams {
  tokenAddress: string
  first?: number
  skip?: number
}

export interface UseTokenHoldersResult {
  data: TokenHolderData[] | null
  isLoading: boolean
  isFetching: boolean
  error: Error | null
  refetch: () => Promise<QueryObserverResult<TokenHolderData[]>>
}

/**
 * Pulls all token holders from the telemetry subgraph
 * @param params search, filter, and pagination params
 * @returns token holders data and query state
 */
export function useTokenHolders(
  params: UseTokenHoldersParams,
): UseTokenHoldersResult {
  const { tokenAddress, first = 1000, skip = 0 } = params

  const queryResult = useQuery({
    queryKey: ['tokenHolders', tokenAddress, first, skip],
    queryFn: async () => {
      const result = await graphql_telemetry_call({
        query: query_get_token_holders,
        variables: {
          first,
          skip,
          token: tokenAddress,
        },
      })

      return result as TokenHolderData[]
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: Boolean(tokenAddress),
  })

  return {
    data: queryResult.data || null,
    isLoading: queryResult.isLoading,
    isFetching: queryResult.isFetching,
    error: queryResult.error,
    refetch: queryResult.refetch,
  }
}
